package com.evoapps.evoshift.domain.usecase.finance

import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.usecase.auth.CallerCapabilities
import com.evoapps.evoshift.domain.usecase.auth.allows
import com.evoapps.evoshift.domain.model.DailyRecord
import com.evoapps.evoshift.domain.repository.FinancialRepository
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.number
import kotlinx.datetime.plus

/**
 * Recomputes the daily record of every day of a roster's month from the current shifts and costs,
 * keeping what the manager entered by hand (takings, plan override, comments) and the lock flag.
 * The month's data is read once (all collections at the same time) and the records are written
 * in one batch.
 */
class RegenerateDailyRecordsUseCase(
    private val financialRepository: FinancialRepository,
    private val calculateDailyRecordUseCase: CalculateDailyRecordUseCase,
    /** Daily records are money (VIEW_FINANCES): skipped for others, computed when a financial manager opens or publishes the month. */
    private val caller: CallerCapabilities? = null
) {
    suspend operator fun invoke(workplaceId: String, rosterId: String, year: Int, month: Int): Result<List<DailyRecord>> = runCatching {
        if (!caller.allows(workplaceId, Capability.VIEW_FINANCES)) return@runCatching emptyList()
        val (inputs, existing) = coroutineScope {
            val inputs = async { calculateDailyRecordUseCase.load(workplaceId, rosterId).getOrThrow() }
            val existing = async { existingRecords(rosterId) }
            inputs.await() to existing.await()
        }
        val records = build(workplaceId, rosterId, year, month, inputs, existing)
        financialRepository.saveDailyRecords(records).getOrThrow()
        records
    }

    /**
     * What the records of a month being created are computed from, read before the month exists
     * (CreateRosterUseCase): the workplace's targets, last year and records kept from before. Its
     * shifts have no cost snapshots yet and its costs are the ones being materialised, which the
     * caller passes to [build]. The caller has checked VIEW_FINANCES.
     */
    suspend fun loadForNewMonth(workplaceId: String, rosterId: String): Result<Pair<DailyRecordInputs, Map<String, DailyRecord>>> = runCatching {
        coroutineScope {
            val inputs = async { calculateDailyRecordUseCase.load(workplaceId, rosterId, withRosterData = false).getOrThrow() }
            val existing = async { existingRecords(rosterId) }
            inputs.await() to existing.await()
        }
    }

    private suspend fun existingRecords(rosterId: String): Map<String, DailyRecord> =
        financialRepository.getDailyRecordsForRoster(rosterId).getOrDefault(emptyList()).associateBy { it.date }

    /** Every day's record of the month from [inputs], keeping the manual values and lock of [existing] ones. */
    fun build(
        workplaceId: String,
        rosterId: String,
        year: Int,
        month: Int,
        inputs: DailyRecordInputs,
        existing: Map<String, DailyRecord>
    ): List<DailyRecord> = generateSequence(LocalDate(year, month, 1)) { it.plus(DatePeriod(days = 1)) }
        .takeWhile { it.month.number == month }
        .map { date ->
            val previous = existing[date.toString()]
            calculateDailyRecordUseCase.compute(
                workplaceId, rosterId, date.toString(), inputs,
                DailyRevenueInput.keptFrom(previous)
            ).copy(isLocked = previous?.isLocked ?: false)
        }
        .toList()

    /** Writes [records] (one batch per 450). */
    suspend fun save(records: List<DailyRecord>): Result<Unit> = financialRepository.saveDailyRecords(records)
}
